import { Flex, Select } from "antd";
import { useEffect, type FC } from "react";
import PlotsComponent from "../../../../shared/layout/Plots";
import { useNewTrialState } from "../../hooks/NewTrialStateProvider";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { asNonEmpty } from "../../../../shared/utils/stringUtils";
import StandardSeedRate from "../../../../shared/layout/StandardSeedRate";
import type { RatesDosageDataInterface } from "../../types";

export const RateUnitDropDown = (): React.ReactElement => {
  const { t } = useTranslation();
  const { setRatesDosage, ratesDosage, trialInfo } = useNewTrialState();
  const { trialProtocolData } = useNewTrialState();

  const measureUnitFromSavedTrial =
    trialInfo?.trial_plots?.[0]?.properties?.seeds?.measure_unit;

  const measureUnitFromProtocol =
    trialProtocolData?.product_details?.seeds?.measure_unit;

  const unit =
    asNonEmpty(ratesDosage.unit_of_rate) ??
    asNonEmpty(measureUnitFromSavedTrial) ??
    asNonEmpty(measureUnitFromProtocol) ??
    "seeds/ha";

  const normalizeUnit = (value: string | null | undefined): string =>
    (value ?? "")
      .toLowerCase()
      .replace("\u00b2", "2")
      .replace(/\s+/g, "")
      .trim();

  const toNumberIfPossible = (
    value: number | string | null | undefined
  ): number | null | undefined => {
    if (value == null) return value;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const convertRate = (
    rate: number | string | null | undefined,
    fromUnit: string,
    toUnit: string
  ): number | null | undefined => {
    const numericRate = toNumberIfPossible(rate);
    if (numericRate == null) return numericRate;

    const normalizedFrom = normalizeUnit(fromUnit);
    const normalizedTo = normalizeUnit(toUnit);

    if (normalizedFrom === normalizedTo) return numericRate;

    if (normalizedFrom === "seeds/ha" && normalizedTo === "seeds/m2") {
      return numericRate / 10000;
    }

    if (normalizedFrom === "seeds/m2" && normalizedTo === "seeds/ha") {
      return numericRate * 10000;
    }

    return numericRate;
  };

  const convertPlotGroup = (
    group: RatesDosageDataInterface[],
    fromUnit: string,
    toUnit: string
  ): RatesDosageDataInterface[] =>
    group.map((plot) => ({
      // Prefer persisted row unit when present to avoid mismatches in edit flow.
      ...(plot as any),
      rate:
        convertRate(
          plot?.rate,
          asNonEmpty((plot as any)?.measure_unit) ?? fromUnit,
          toUnit
        ) ?? undefined,
      measure_unit: toUnit,
    }));

  const getFirstPlotUnit = (
    ratesAndDosages: unknown[] | undefined
  ): string | undefined => {
    if (!Array.isArray(ratesAndDosages)) return undefined;

    for (const group of ratesAndDosages) {
      if (!Array.isArray(group) || group.length === 0) continue;
      const firstPlot = group[0] as { measure_unit?: string } | undefined;
      const unitFromPlot = asNonEmpty(firstPlot?.measure_unit);
      if (unitFromPlot) return unitFromPlot;
    }

    return undefined;
  };

  const handleUnitChange = (value: string): void => {
    setRatesDosage((prevRatesDosage) => {
      const fromUnitFromState =
        asNonEmpty(prevRatesDosage?.unit_of_rate) ?? unit;
      const fromUnit =
        getFirstPlotUnit(prevRatesDosage?.rates_and_dosages) ??
        fromUnitFromState;
      const toUnit = value;

      const convertedStandardSeedRate = convertRate(
        prevRatesDosage?.standard_seed_rate,
        fromUnit,
        toUnit
      );

      const convertedRatesAndDosages =
        prevRatesDosage?.rates_and_dosages?.map(
          (group: RatesDosageDataInterface[]) =>
            Array.isArray(group)
              ? convertPlotGroup(group, fromUnit, toUnit)
              : group
        ) ?? [];

      return {
        ...prevRatesDosage,
        unit_of_rate: toUnit,
        standard_seed_rate:
          convertedStandardSeedRate ?? prevRatesDosage.standard_seed_rate,
        rates_and_dosages: convertedRatesAndDosages,
      };
    });
  };

  useEffect(() => {
    handleUnitChange(unit);
  }, [unit]);

  return (
    <div className="rating-dropdown-container">
      <div className="rates-select">
        <h3 className="rates-title">
          {formatTranslation(t("newTrial.ratesDosage.rates"))}
        </h3>
        <Select
          value={asNonEmpty(ratesDosage?.unit_of_rate) ?? unit}
          onChange={(value) => {
            handleUnitChange(value);
          }}
        >
          <Select.Option
            value="seeds/ha"
            disabled={ratesDosage?.unit_of_rate === "kg/ha"}
          >
            {formatTranslation(t("newTrial.ratesDosage.unit1"))}
          </Select.Option>
          <Select.Option
            value="seeds/m2"
            disabled={ratesDosage?.unit_of_rate === "kg/ha"}
          >
            {formatTranslation(t("newTrial.ratesDosage.unit2"))}
          </Select.Option>
          <Select.Option
            value="kg/ha"
            disabled={ratesDosage?.unit_of_rate?.includes("seeds/")}
          >
            {formatTranslation(t("newTrial.ratesDosage.unit3"))}
          </Select.Option>
        </Select>
      </div>
    </div>
  );
};

const SeedsComponent: FC = () => {
  return (
    <div className="rates-dosage">
      <Flex justify="end">
        <RateUnitDropDown />
      </Flex>
      <StandardSeedRate />
      <PlotsComponent from={"seeds"} />
    </div>
  );
};

export default SeedsComponent;
